// Page-memory recovery only. No storage, automatic eviction, or assumption that a
// timed-out/failed connection stopped the server's write.
export const SAVE_WAIT_MS = 30000;
export const MAX_RECOVERY_DRAFTS = 100;

export const createFeedbackDraftState = ({ target = typeof window === 'undefined' ? null : window } = {}) => {
  const drafts = new Map();
  const writes = new Map();
  const listeners = new Map();
  const acknowledgements = new WeakSet();
  let isDisposed = false;
  let isWarningAttached = false;

  const warnBeforeUnload = (event) => {
    if (drafts.size || writes.size) {
      event.preventDefault();
      event.returnValue = '';
    }
  };
  const updateWarning = () => {
    const isNeeded = !isDisposed && Boolean(drafts.size || writes.size);
    if (isNeeded && !isWarningAttached) {
      target?.addEventListener('beforeunload', warnBeforeUnload);
    }
    if (!isNeeded && isWarningAttached) {
      target?.removeEventListener('beforeunload', warnBeforeUnload);
    }
    isWarningAttached = isNeeded;
  };
  const notify = (key, event) => {
    if (!isDisposed) {
      listeners.get(key)?.forEach((listener) => {
        listener(event);
      });
    }
    updateWarning();
  };
  const getWrite = (key) => {
    const write = writes.get(key);
    return write ? { pending: write.pending, uncertain: write.uncertain } : null;
  };
  const retain = (key, draft) => {
    if (isDisposed || (!drafts.has(key) && drafts.size >= MAX_RECOVERY_DRAFTS)) {
      return false;
    }
    drafts.set(key, { ...draft });
    updateWarning();
    return true;
  };
  const release = (key) => {
    drafts.delete(key);
    updateWarning();
  };

  const runWrite = (key, value, execute) => {
    const existing = getWrite(key);
    if (existing) {
      return Promise.resolve({ error: true, waiting: true, ...existing });
    }
    if (isDisposed || writes.size >= MAX_RECOVERY_DRAFTS) {
      return Promise.resolve({ error: true, capacity: true });
    }
    let resolveOutcome;
    const outcome = new Promise((resolve) => {
      resolveOutcome = resolve;
    });
    const write = { pending: true, uncertain: false };
    writes.set(key, write);
    const timeout = setTimeout(() => {
      if (isDisposed || writes.get(key) !== write) {
        return;
      }
      write.uncertain = true;
      resolveOutcome({ error: true, waiting: true, uncertain: true, pending: true });
      notify(key, { type: 'uncertain' });
    }, SAVE_WAIT_MS);
    write.timeout = timeout;
    updateWarning();

    Promise.resolve()
      .then(execute)
      .catch(() => {
        return { error: true, uncertain: true };
      })
      .then((result) => {
        clearTimeout(timeout);
        if (isDisposed || writes.get(key) !== write) {
          return;
        }
        if (result.uncertain) {
          // A rejected transport may leave the server operation running. Keep a small
          // fence, not a growing chain of queued closures, and never start a retry.
          write.pending = false;
          write.uncertain = true;
        } else {
          writes.delete(key);
          if (!result.error) {
            const draft = drafts.get(key);
            if (draft) {
              if (draft.value === value && draft.conflictValue === undefined) {
                drafts.delete(key);
              } else {
                drafts.set(key, { ...draft, baseValue: value });
              }
            }
          }
        }
        resolveOutcome(result);
        notify(key, { type: result.uncertain ? 'uncertain' : 'available', result, value });
      });
    return outcome;
  };

  return {
    getDraft: (key) => {
      const draft = drafts.get(key);
      return draft ? { ...draft } : null;
    },
    getWrite,
    retain,
    release,
    runWrite,
    subscribe: (key, listener) => {
      if (!listeners.has(key)) {
        listeners.set(key, new Set());
      }
      listeners.get(key).add(listener);
      return () => {
        listeners.get(key)?.delete(listener);
        if (listeners.get(key)?.size === 0) {
          listeners.delete(key);
        }
      };
    },
    markAcknowledgement: (question) => {
      acknowledgements.add(question);
      return question;
    },
    isAcknowledgement: (question) => {
      return acknowledgements.has(question);
    },
    // A page owner may dispose its isolated state on teardown; disposal never
    // releases an old write into another state instance's queue.
    dispose: () => {
      isDisposed = true;
      writes.forEach((write) => {
        clearTimeout(write.timeout);
      });
      drafts.clear();
      writes.clear();
      listeners.clear();
      updateWarning();
    },
  };
};

export const feedbackDraftState = createFeedbackDraftState();
